// src/components/JournalView.jsx
//
// "My Journal": what the member has started (top), then the library of
// interactive checklists / worksheets as FOLDERS — one per topic, grouped
// into Mental Health / Mental Fitness / Body & Recovery — so 30+ sheets read
// as a handful of rows instead of a wall of cards. Entries are self-only
// (users/{uid}/private_journal). Also home of the AI Coach opt-in — off by
// default; when on, a compact summary of the journal travels with coach
// messages (see hooks/useJournalContext.js).

import React, { useEffect, useMemo, useState } from 'react';
import { BookOpenCheck, CheckCircle2, ChevronDown, ChevronRight, ClipboardList, PenLine, Sparkles, Lock, Folder, FolderOpen, Heart, Brain, Activity } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { useOrganization } from '../context/OrganizationContext';
import { useOrgVoice } from '../hooks/useOrgVoice';
import { WORKSHEETS } from '../data/worksheets';
import { initialTopicContent } from '../data/content';
import { subscribeJournal } from '../utils/journalStore';
import { PREVIEW_UID } from '../utils/guidedPath';
import { WorksheetOverlay } from './worksheets/WorksheetRunner';
import { topicIds } from '../data/navTopics';
import { isTabAllowed } from '../utils/navAccess';
import { resolveAgeTier } from '../utils/ageTier';

// Folder groups (topic ids match content.js / guidedPathRoutes). The journal
// groups by what a sheet is ABOUT, which is not quite the sidebar's grouping
// (nutrition and pain sit under Body here), so the groups stay local. Which
// folders an org actually OFFERS is not local: every folder is filtered through
// utils/navAccess.js below, the same list the sidebar and router use, so an org
// offering three Mental Health topics gets three Mental Health folders.
const GROUPS = [
    { id: 'mental-health', labelKey: 'navMentalHealth', icon: Heart, topics: topicIds('mental-health') },
    { id: 'mental-performance', labelKey: 'navMentalPerformance', icon: Brain, topics: ['resilience', 'focus', 'confidence', 'motivation', 'self-discipline', 'self-awareness', 'adaptability'] },
    { id: 'body', label: 'Body & Recovery', icon: Activity, topics: ['sleep', 'nutrition-hydration', 'pain-injury'] }
];

const JournalView = ({ currentUser, onNavigate }) => {
    const { isPreviewMode, orgData } = useOrganization();
    const { t } = useOrgVoice();
    const uid = isPreviewMode ? PREVIEW_UID : currentUser?.uid;
    const [entries, setEntries] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [openFolders, setOpenFolders] = useState(null); // Set of topic ids; null = not initialised
    // Opt-in lives on the user doc; preview mode keeps it local
    const [previewOptIn, setPreviewOptIn] = useState(false);
    const optIn = isPreviewMode ? previewOptIn : !!currentUser?.journalShareWithCoach;
    const [savingOptIn, setSavingOptIn] = useState(false);

    useEffect(() => {
        if (!uid) return undefined;
        const unsub = subscribeJournal(uid, setEntries);
        return () => unsub();
    }, [uid]);

    const byId = useMemo(() => Object.fromEntries(entries.map(e => [e.worksheetId || e.id, e])), [entries]);
    const started = useMemo(() => Object.values(WORKSHEETS).filter(w => byId[w.id]), [byId]);
    const byTopic = useMemo(() => {
        const map = {};
        Object.values(WORKSHEETS).forEach(w => { (map[w.topicId] = map[w.topicId] || []).push(w); });
        Object.values(map).forEach(list => list.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'checklist' ? -1 : 1)));
        return map;
    }, []);
    const topicTitle = (id) => initialTopicContent[id]?.title || id;

    // Folders with something in progress open by default (once entries have loaded)
    useEffect(() => {
        if (openFolders !== null) return;
        if (uid && entries.length === 0) return; // wait for the first snapshot
        setOpenFolders(new Set(started.map(w => w.topicId)));
    }, [openFolders, uid, entries.length, started]);
    const folderOpen = (topicId) => !!openFolders && openFolders.has(topicId);
    const toggleFolder = (topicId) => setOpenFolders(prev => {
        const next = new Set(prev || []);
        if (next.has(topicId)) next.delete(topicId); else next.add(topicId);
        return next;
    });

    const setOptIn = async (value) => {
        if (isPreviewMode) { setPreviewOptIn(value); return; }
        if (!uid) return;
        setSavingOptIn(true);
        try {
            await setDoc(doc(db, 'users', uid), { journalShareWithCoach: value }, { merge: true });
        } catch (e) {
            console.error('opt-in save failed', e);
        } finally {
            setSavingOptIn(false);
        }
    };

    const openDef = openId ? WORKSHEETS[openId] : null;

    const Card = ({ w, entry, compact = false }) => {
        const Icon = w.kind === 'checklist' ? ClipboardList : PenLine;
        const pct = entry?.progress?.pct ?? 0;
        return (
            <button onClick={() => setOpenId(w.id)} className={`text-left bg-white rounded-2xl border border-slate-200 hover:border-brand-primary hover:shadow-md transition-all group ${compact ? 'p-4' : 'p-5'}`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><Icon size={18} /></div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{compact ? w.kind : `${topicTitle(w.topicId)} · ${w.kind}`}</p>
                            <h3 className="font-black text-slate-900 leading-tight text-sm">{w.title}</h3>
                        </div>
                    </div>
                    <ChevronRight size={18} className="text-slate-300 group-hover:text-brand-primary shrink-0 mt-1" />
                </div>
                {entry ? (
                    <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 mb-1">
                            <span>{pct}% · {entry.progress?.answered ?? 0}/{entry.progress?.total ?? 0}</span>
                            {entry.completedAt
                                ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Completed</span>
                                : <span>Updated {entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : '—'}</span>}
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${entry.completedAt ? 'bg-emerald-500' : 'bg-brand-primary'}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-slate-500 font-medium mt-2">Not started — tap to begin.</p>
                )}
            </button>
        );
    };

    const TopicFolder = ({ topicId }) => {
        const sheets = byTopic[topicId] || [];
        if (sheets.length === 0) return null;
        const inProgress = sheets.filter(w => byId[w.id] && !byId[w.id].completedAt).length;
        const done = sheets.filter(w => byId[w.id]?.completedAt).length;
        const open = folderOpen(topicId);
        const FIcon = open ? FolderOpen : Folder;
        return (
            <div className={`rounded-2xl border transition-colors ${open ? 'bg-white border-brand-primary/30' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                <button onClick={() => toggleFolder(topicId)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                    <FIcon size={18} className={open ? 'text-brand-primary shrink-0' : 'text-slate-400 shrink-0'} />
                    <span className="font-bold text-slate-800 text-sm flex-1 min-w-0 truncate">{topicTitle(topicId)}</span>
                    <span className="text-[11px] font-bold text-slate-400 shrink-0 hidden sm:inline">{sheets.length} sheet{sheets.length === 1 ? '' : 's'}</span>
                    {inProgress > 0 && <span className="text-[10px] font-black text-brand-primary bg-brand-light px-2 py-0.5 rounded-full shrink-0">{inProgress} in progress</span>}
                    {done > 0 && <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1"><CheckCircle2 size={10} /> {done}</span>}
                    <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                    <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in duration-200">
                        {sheets.map(w => <Card key={w.id} w={w} entry={byId[w.id]} compact />)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500 pb-20">
            {openDef && (
                <WorksheetOverlay worksheetId={openId} currentUser={currentUser} topicId={openDef.topicId} onClose={() => setOpenId(null)} />
            )}

            {/* HERO */}
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-light/60 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-brand-light text-brand-primary text-xs font-bold uppercase tracking-wider rounded-full flex items-center gap-1"><Lock size={12} /> Private to you</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-2"><BookOpenCheck className="text-brand-primary" size={28} /> My Journal</h1>
                    <p className="text-slate-600 font-medium mt-1 max-w-xl">Your checklists and worksheets, saved as you go. No one else in your organization can see what you write here.</p>
                </div>
            </div>

            {/* AI COACH OPT-IN */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Sparkles size={20} /></div>
                    <div>
                        <p className="font-black text-slate-900">Let my AI Coach see my journal</p>
                        <p className="text-xs text-slate-500 font-medium mt-0.5 max-w-lg">Off by default. When on, a short summary of your answers travels with your coach messages so it can give more specific suggestions. Switch it off any time — nothing is shared with your organization either way.</p>
                    </div>
                </div>
                <button
                    type="button" role="switch" aria-checked={optIn} disabled={savingOptIn}
                    onClick={() => setOptIn(!optIn)}
                    className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${optIn ? 'bg-indigo-600' : 'bg-slate-300'} ${savingOptIn ? 'opacity-60' : ''}`}
                >
                    <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${optIn ? 'left-7' : 'left-1'}`} />
                </button>
            </div>

            {/* IN PROGRESS */}
            <div>
                <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 px-1">In progress</h2>
                {started.length === 0 ? (
                    <div className="p-6 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-sm text-slate-500 font-medium">Nothing started yet — open a folder below and pick a sheet.</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {started.map(w => <Card key={w.id} w={w} entry={byId[w.id]} />)}
                    </div>
                )}
            </div>

            {/* LIBRARY — folders by topic, grouped */}
            {GROUPS.map(group => {
                // A folder shows when it has sheets AND the org offers that topic.
                // "In progress" above is deliberately NOT filtered: those are the
                // member's own entries, and a menu change must never hide someone's
                // own writing from them.
                const topics = group.topics.filter(id => (byTopic[id] || []).length > 0 && isTabAllowed(orgData, id, resolveAgeTier(currentUser)));
                if (topics.length === 0) return null;
                const GIcon = group.icon;
                return (
                    <div key={group.id}>
                        <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 px-1 flex items-center gap-2"><GIcon size={14} /> {group.labelKey ? t(group.labelKey) : group.label}</h2>
                        <div className="space-y-2">
                            {topics.map(id => <TopicFolder key={id} topicId={id} />)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default JournalView;
